/**
 * Data models for Vedic astrology calculations.
 *
 * Python dataclasses → interfaces + factory functions (PORTING.md). `to_dict`
 * methods become standalone `*_to_dict` functions; field names keep snake_case.
 */

import type { PlainDateTime } from "../lib/datetime";
import { pythonRound } from "../lib/pyutil";
import { SIDEREAL_YEAR_DAYS } from "./constants";

/** Natal promise strength levels. */
export enum PromiseStrength {
  NONE = 0, // No natal support - cap metrics toward neutral
  WEAK = 1, // Some support - moderate potential
  STRONG = 2, // Strong natal promise - full transit activation
}

/** Functional benefic/malefic role based on lagna. */
export enum FunctionalRole {
  MALEFIC = -1,
  NEUTRAL = 0,
  BENEFIC = 1,
  YOGAKARAKA = 2, // Rules both trine and kendra
}

/** Represents a value with associated confidence/uncertainty. */
export interface UncertainValue {
  value: number;
  confidence: number;
  sources: string[];
}

export function createUncertainValue(value: number, confidence: number, sources: string[] = []): UncertainValue {
  // __post_init__: clamp confidence to [0, 1]
  return { value, confidence: Math.max(0.0, Math.min(1.0, confidence)), sources };
}

export function uncertain_value_to_dict(v: UncertainValue): Record<string, any> {
  return {
    value: pythonRound(v.value, 3),
    confidence: pythonRound(v.confidence, 3),
    sources: v.sources,
  };
}

/** Raw planetary position data - no interpretation. */
export interface PlanetPosition {
  longitude: number;
  sign: number;
  sign_name: string;
  degree_in_sign: number;
  nakshatra: number;
  nakshatra_name: string;
  nakshatra_pada: number;
  speed: number;
  is_retrograde: boolean;
}

export function planet_position_to_dict(p: PlanetPosition): Record<string, any> {
  return {
    longitude: pythonRound(p.longitude, 4),
    sign: p.sign,
    sign_name: p.sign_name,
    degree_in_sign: pythonRound(p.degree_in_sign, 2),
    nakshatra: p.nakshatra,
    nakshatra_name: p.nakshatra_name,
    nakshatra_pada: p.nakshatra_pada,
    speed: pythonRound(p.speed, 4),
    is_retrograde: p.is_retrograde,
  };
}

/** Vedic Graha Drishti (planetary aspect) information. */
export interface DrishtiData {
  aspecting_planet: string;
  aspected_planet: string;
  aspecting_house: number;
  aspected_house: number;
  house_distance: number;
  is_full_aspect: boolean;
  aspect_strength: number;
  aspecting_retrograde: boolean;
}

export function drishti_data_to_dict(d: DrishtiData): Record<string, any> {
  return {
    aspecting: d.aspecting_planet,
    aspected: d.aspected_planet,
    from_house: d.aspecting_house,
    to_house: d.aspected_house,
    distance: d.house_distance,
    full_aspect: d.is_full_aspect,
    strength: pythonRound(d.aspect_strength, 2),
    retrograde: d.aspecting_retrograde,
  };
}

// Backward-compat shim only. Do not use for scoring; use DrishtiData instead.
// This codebase’s ONLY supported aspect system is Graha Drishti via DrishtiData
// produced by astro.drishti.calculate_graha_drishti (house-distance based).
/** DEPRECATED: Legacy angle-based aspect model retained for backward compatibility only. */
export interface AspectData {
  transit_planet: string;
  natal_planet: string;
  aspect_angle: number;
  orb_offset: number;
  is_applying: boolean;
  transit_retrograde: boolean;
}

export function aspect_data_to_dict(a: AspectData): Record<string, any> {
  return {
    transit: a.transit_planet,
    natal: a.natal_planet,
    angle: a.aspect_angle,
    orb: pythonRound(a.orb_offset, 2),
    applying: a.is_applying,
    retrograde: a.transit_retrograde,
  };
}

/** Current dasha period information with precise timing — all 5 Vimshottari levels. */
export interface DashaInfo {
  mahadasha_lord: string;
  antardasha_lord: string;
  pratyantardasha_lord: string | null;
  maha_days_remaining: number;
  antar_days_remaining: number;
  maha_start_jd: number;
  maha_end_jd: number;
  antar_start_jd: number;
  antar_end_jd: number;
  // Sub-antar levels (populated by get_current_dasha)
  pratyantar_days_remaining: number | null;
  sukshma_lord: string | null;
  sukshma_days_remaining: number | null;
  prana_lord: string | null;
  prana_days_remaining: number | null;
  pratyantar_start_jd: number | null;
  pratyantar_end_jd: number | null;
  sukshma_start_jd: number | null;
  sukshma_end_jd: number | null;
  prana_start_jd: number | null;
  prana_end_jd: number | null;
}

/** Return the active dasha lord at the requested hierarchy level. */
export function dasha_lord_at_level(info: DashaInfo, level: string): string | null | undefined {
  return ({
    maha: info.mahadasha_lord,
    antar: info.antardasha_lord,
    pratyantar: info.pratyantardasha_lord,
    sukshma: info.sukshma_lord,
    prana: info.prana_lord,
  } as Record<string, string | null>)[level];
}

export function dasha_info_to_dict(d: DashaInfo): Record<string, any> {
  return {
    mahadasha: d.mahadasha_lord,
    antardasha: d.antardasha_lord,
    pratyantardasha: d.pratyantardasha_lord,
    sukshmantar: d.sukshma_lord,
    prana: d.prana_lord,
    maha_days_remaining: pythonRound(d.maha_days_remaining, 2),
    maha_years_remaining: pythonRound(d.maha_days_remaining / SIDEREAL_YEAR_DAYS, 2),
    antar_days_remaining: pythonRound(d.antar_days_remaining, 2),
    antar_years_remaining: pythonRound(d.antar_days_remaining / SIDEREAL_YEAR_DAYS, 2),
    pratyantar_days_remaining:
      d.pratyantar_days_remaining !== null ? pythonRound(d.pratyantar_days_remaining, 2) : null,
    sukshma_days_remaining:
      d.sukshma_days_remaining !== null ? pythonRound(d.sukshma_days_remaining, 2) : null,
    prana_days_remaining: d.prana_days_remaining !== null ? pythonRound(d.prana_days_remaining, 2) : null,
  };
}

/** Simplified Shadbala strength factors for a planet. */
export interface ShadbalaLite {
  dignity_score: number; // Sthana bala proxy (exalt/own/debil)
  dig_bala: number; // Directional strength (angular placement)
  cheshta_bala: number; // Motional strength (retrograde strong / fast weak; luminaries: Paksha/Ayan)
  naisargika_bala: number; // Natural inherent strength (05_shadbala_chesht_naisargika_drik_bala.md)
  total: number; // Weighted composite
}

export function createShadbalaLite(init: Partial<ShadbalaLite> = {}): ShadbalaLite {
  return {
    dignity_score: init.dignity_score ?? 0.5,
    dig_bala: init.dig_bala ?? 0.5,
    cheshta_bala: init.cheshta_bala ?? 0.5,
    naisargika_bala: init.naisargika_bala ?? 0.5,
    total: init.total ?? 0.5,
  };
}

export function shadbala_lite_to_dict(s: ShadbalaLite): Record<string, number> {
  return {
    dignity: pythonRound(s.dignity_score, 3),
    dig_bala: pythonRound(s.dig_bala, 3),
    cheshta_bala: pythonRound(s.cheshta_bala, 3),
    naisargika: pythonRound(s.naisargika_bala, 3),
    total: pythonRound(s.total, 3),
  };
}

/** Bhava (house) strength based on distance from center. */
export interface BhavaStrength {
  house: number;
  cusp_longitude: number;
  center_longitude: number; // Bhava madhya
  planet_distance_factor: Record<string, number>;
}

export function bhava_strength_to_dict(b: BhavaStrength): Record<string, any> {
  return {
    house: b.house,
    cusp: pythonRound(b.cusp_longitude, 2),
    center: pythonRound(b.center_longitude, 2),
    planet_factors: Object.fromEntries(
      Object.entries(b.planet_distance_factor).map(([k, v]) => [k, pythonRound(v, 3)])
    ),
  };
}

/** Natal promise strengths for each life area. */
export interface NatalPromises {
  career: PromiseStrength;
  relationships: PromiseStrength;
  health: PromiseStrength;
  finance: PromiseStrength;
  spiritual: PromiseStrength;
  education: PromiseStrength;
  children: PromiseStrength;
  transformation: PromiseStrength;
}

export function createNatalPromises(init: Partial<NatalPromises> = {}): NatalPromises {
  return {
    career: init.career ?? PromiseStrength.WEAK,
    relationships: init.relationships ?? PromiseStrength.WEAK,
    health: init.health ?? PromiseStrength.WEAK,
    finance: init.finance ?? PromiseStrength.WEAK,
    spiritual: init.spiritual ?? PromiseStrength.WEAK,
    education: init.education ?? PromiseStrength.WEAK,
    children: init.children ?? PromiseStrength.WEAK,
    transformation: init.transformation ?? PromiseStrength.WEAK,
  };
}

export function natal_promises_get(p: NatalPromises, area: string): PromiseStrength {
  const v = (p as unknown as Record<string, PromiseStrength>)[area.toLowerCase()];
  return v ?? PromiseStrength.WEAK;
}

/** Divisional chart support factors - computed once from natal chart. */
export interface DivisionalSupport {
  d9_relationship_strength: number;
  d10_career_strength: number;
  d2_wealth_strength: number;
  d4_fortune_strength: number;
  d7_children_strength: number;
  d20_spiritual_strength: number;
  d24_education_strength: number;
  d30_difficulties_vulnerability: number;
  d60_karmic_intensity: number;
}

export function createDivisionalSupport(init: Partial<DivisionalSupport> = {}): DivisionalSupport {
  return {
    d9_relationship_strength: init.d9_relationship_strength ?? 0.5,
    d10_career_strength: init.d10_career_strength ?? 0.5,
    d2_wealth_strength: init.d2_wealth_strength ?? 0.5,
    d4_fortune_strength: init.d4_fortune_strength ?? 0.5,
    d7_children_strength: init.d7_children_strength ?? 0.5,
    d20_spiritual_strength: init.d20_spiritual_strength ?? 0.5,
    d24_education_strength: init.d24_education_strength ?? 0.5,
    d30_difficulties_vulnerability: init.d30_difficulties_vulnerability ?? 0.5,
    d60_karmic_intensity: init.d60_karmic_intensity ?? 0.5,
  };
}

/** Functional benefic/malefic roles for all planets based on lagna. */
export interface FunctionalRoles {
  roles: Record<string, FunctionalRole>;
  yogakarakas: string[];
}

export function functional_roles_get_role(fr: FunctionalRoles, planet: string): FunctionalRole {
  return fr.roles[planet.toLowerCase()] ?? FunctionalRole.NEUTRAL;
}

export function functional_roles_is_yogakaraka(fr: FunctionalRoles, planet: string): boolean {
  return fr.yogakarakas.includes(planet.toLowerCase());
}

/** Daily theme with advice for narrative output. */
export interface DailyTheme {
  theme_name: string;
  intensity: number; // 0-1
  description: string;
  advice: string[];
  supporting_factors: string[];
  // Interpretation-only fields (no astrology/scoring impact)
  feels_like: string[];
  avoid: string[];
  helps: string[];
  // Interpretation-only confidence display (band is derived)
  confidence: number;
  confidence_band: string; // High | Medium | Low
}

/** Complete raw chart data - NO interpretation applied. */
export interface RawChartData {
  datetime_utc: PlainDateTime;
  datetime_local: PlainDateTime;
  julian_day: number;
  ayanamsa: number;
  planets: Record<string, PlanetPosition>;
  house_cusps: number[];
  ascendant_longitude: number;
  midheaven_longitude: number;
  retrograde_planets: string[];
  house_positions: Record<string, number>;
  bhava_centers: number[]; // Bhava madhya
  placidus_cusps: number[]; // Sidereal Placidus cusps (for KP sub-lords)
  shadbala: Record<string, ShadbalaLite>;
  functional_roles: FunctionalRoles | null;
}
